/**
 * Top bar for the SHSAT Adaptive test-taking page.
 *
 * Mirrors the legacy prod SHSAT header (Module/index.tsx ~1766-2058)
 * 1:1, minus the elements the SHSAT-adaptive product owner explicitly
 * removed:
 *   - prev/next arrow buttons (no-skip rule — students cannot go back)
 *   - Review button (no per-question marker review on adaptive)
 *   - Bookmark button (same)
 *
 * What IS mirrored:
 *   - Function group: Pointer / Answer Eliminator / Global Notepad
 *     buttons. Visual selected state + onChange callback; the actual
 *     tool wiring (cross-out persistence, notepad text, line reader DOM
 *     drag) is task #21.
 *   - Timer button (countdown + show/hide), persisted to localStorage.
 *   - Student name display.
 *   - User dropdown with Show Line Reader Mask, Save and Exit, and
 *     Discard and Exit.
 *   - Blue header boundary stripe + dark gray ``headerBottom`` info bar
 *     with test name / section / current question number.
 *
 * The CSS classes are inherited from ``Module/index.css`` — no new
 * styling is added here, per the style-mirror guidance in
 * ``memory/feedback_shsat_adaptive_style_mirror.md``.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TfiTimer } from 'react-icons/tfi';
import { IoMdPerson, IoMdArrowDropdown } from 'react-icons/io';
import { FaArrowPointer, FaBookmark, FaRegBookmark } from 'react-icons/fa6';
import { FaArrowLeft, FaArrowRight, FaCheck } from 'react-icons/fa';
import { RxCross2 } from 'react-icons/rx';
import { PiNote, PiListBulletsBold } from 'react-icons/pi';

import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import { PassageReviewList, PassageReviewEntry, ReviewTab } from '../PassageReviewList';
import { sectionDisplayLabel } from '../sectionOrder';


// Mirrors Module/index.tsx. Each entry is [defaultLabel, activeLabel];
// membership in ``dropdownToolsSelected`` toggles the tool.
const DROPDOWNTOOLS: Array<[string, string]> = [
  ['Enable Magnifier', 'Disable Magnifier'],
  ['Show Line Reader Mask', 'Hide Line Reader Mask'],
];


function formatTime(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = safe % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}


function timerStorageKey(studentTestId: string): string {
  // Test-wide (NOT per-module) so the single total clock keeps counting
  // across module transitions — matching the legacy total-time countdown.
  return `shsatAdaptive:timer:${studentTestId}`;
}


export type AdaptiveTool = 'Pointer' | 'Answer Eliminator' | 'Global Notepad';


export function AdaptiveTopBar(props: {
  testName: string | null;
  sectionName: string | null;
  durationSeconds: number | null;
  studentTestId: string;
  moduleId: string;
  /** Current slot (1-based, within the current module). When null, the
   * bottom strip omits the slot indicator — used during the initial
   * mount before the first serve. */
  slotNumber?: number | null;
  /** First TEST-GLOBAL slot number of the current module (slot numbers
   * are global in BACKEND order: ELA 1..N, Math N+1..2N). Lets the
   * progress bar convert a global slot number into a within-module
   * position, so a Math-first flow still runs 0 -> 100%. When null the
   * bar falls back to the raw global slot number (unchanged behavior). */
  moduleFirstSlotNumber?: number | null;
  /** Sum of slot counts across all modules in the test. */
  totalSlots?: number;
  /** Slot count of the CURRENT module only. Drives the section-level
   * "N OF M" header total so it reads per-section ("MATH / 1 OF 50")
   * instead of test-global ("51 OF 100"). Falls back to totalSlots. */
  moduleSlotCount?: number | null;
  /** Passage-set framing for ELA slots (server ``navigability.passageSet``):
   * ``index``/``count`` -> "PASSAGE SET X OF N" on the section line;
   * ``position``/``size`` -> per-set "n OF m" count. Null for
   * Math/standalone slots, which keep the global "N OF M" label. */
  passageSet?: {
    index: number | null;
    count: number;
    position: number;
    size: number;
  } | null;
  /** Unified stand-alone block framing (server ``navigability.standaloneItems``):
   * shows "<SECTION> – STAND ALONE ITEMS" + per-block "n OF size". Set for every
   * slot in the block (Part A + Part B). Null for reading passages / Math.
   * Takes precedence over passageSet. */
  standaloneItems?: {
    position: number;
    size: number;
  } | null;
  /** Display name for the user button on the right. */
  studentName?: string | null;
  /** Which tool is currently active; ``Pointer`` is the default. */
  toolsSelected?: AdaptiveTool;
  /** Toggle handler — parent owns the source of truth so the body can
   * react (e.g. enabling cross-out hover on the MC choices). */
  onToolChange?: (tool: AdaptiveTool) => void;
  /** Dropdown toggles (e.g. Line Reader Mask). */
  dropdownToolsSelected?: string[];
  onDropdownToolChange?: (tool: string) => void;
  /** Forward-arrow handler — adaptive's "Next" action. */
  onAdvance?: () => void;
  /** Whether the forward arrow is clickable. Mirrors the legacy
   * disabled-on-last-question behavior; for adaptive, also disabled
   * until the student has answered the current slot. */
  canAdvance?: boolean;
  /** Back-arrow handler — re-serves the previous slot in the same
   * passage group. Only enabled when ``canGoBack`` is true. */
  onBack?: () => void;
  /** Server-computed flag: previous slot exists in the same passage
   * group AND was already served. Drives the back arrow's enabled
   * state. */
  canGoBack?: boolean;
  /** The current passage's reachable (served) slots + answered flags. Drives
   * the passage-set Review list's question rows. */
  passageSlots?: { slotNumber: number; answered: boolean }[];
  /** Size of the current passage GROUP (server ``passageGroupSize``). > 1
   * gates the per-passage-set Review + Bookmark tools (reading passage OR
   * Part A). Null/<=1 for single-question / Math slots. */
  passageGroupSize?: number | null;
  /** The slot currently on screen, so Review can highlight it. */
  currentSlotNumber?: number | null;
  /** Jump to a slot within the current passage (re-serves it). */
  onReviewJump?: (slotNumber: number) => void;
  /** Review "Passage End Directions" row → show the end-of-passage interstitial. */
  onReviewJumpToPassageEnd?: () => void;
  /** Whether the student is currently ON the end-of-passage page (highlights
   * the "Passage End Directions" review row). */
  onPassageEndPage?: boolean;
  /** Review "ELA Rdg Comp Directions" row (first reading set only) → show the
   * reading-comprehension directions overlay. */
  onReviewJumpToReadingDirections?: () => void;
  /** Whether the reading-comp directions overlay is currently showing
   * (highlights the "ELA Rdg Comp Directions" review row). */
  onReadingDirectionsPage?: boolean;
  /** Ephemeral per-passage-set bookmark state: slot numbers bookmarked in the
   * CURRENT set (reset by the parent when the passage changes). */
  bookmarkedSlots?: number[];
  /** Whether the CURRENT slot is bookmarked (drives the Bookmark button). */
  isBookmarked?: boolean;
  /** Toggle the current slot's bookmark. */
  onToggleBookmark?: () => void;
  /** Fired once when the (total) clock reaches 0:00 — the parent
   *  auto-submits the whole test, matching legacy timer-expiry behavior. */
  onTimeExpired?: () => void;
  /** Whether the Answer Eliminator applies to the current question
   *  (multiple choice / multiple response only — types 1, 3). Disables
   *  the button on other types, matching legacy. */
  eliminatorEnabled?: boolean;
  /** Opens the container-owned Contrast Settings modal (theming tool). */
  onOpenContrast?: () => void;
  /** "Discard and Exit" — data-destructive discard of the adaptive attempt
   * (parent hits the discard endpoint, clears localStorage, navigates). When
   * omitted the item falls back to a plain exit (navigate back). */
  onDiscardExit?: () => void;
}): JSX.Element {
  const {
    testName,
    sectionName,
    durationSeconds,
    studentTestId,
    moduleId,
    slotNumber,
    moduleFirstSlotNumber = null,
    totalSlots = 0,
    moduleSlotCount = null,
    passageSet = null,
    standaloneItems = null,
    studentName,
    toolsSelected = 'Pointer',
    onToolChange,
    dropdownToolsSelected = [],
    onDropdownToolChange,
    onAdvance,
    canAdvance = false,
    onBack,
    canGoBack = false,
    passageSlots = [],
    passageGroupSize = null,
    currentSlotNumber = null,
    onReviewJump,
    onReviewJumpToPassageEnd,
    onPassageEndPage = false,
    onReviewJumpToReadingDirections,
    onReadingDirectionsPage = false,
    bookmarkedSlots = [],
    isBookmarked = false,
    onToggleBookmark,
    onTimeExpired,
    eliminatorEnabled = false,
    onOpenContrast,
    onDiscardExit,
  } = props;

  const navigate = useNavigate();
  const storageKey = timerStorageKey(studentTestId);
  // Fire onTimeExpired exactly once even though the timer effect re-runs.
  const expiredRef = useRef(false);

  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [reviewTab, setReviewTab] = useState<ReviewTab>('All Questions');
  const reviewRef = useOutsideClickHandler(() => setIsReviewOpen(false));
  // Per-passage-set Review + Bookmark tools show for a MULTI-question passage
  // set (reading passage OR Part A), from the first question — gated on the
  // server-supplied group size. Single-question / Math slots get neither.
  const showPassageTools = (passageGroupSize ?? 0) > 1;

  // Review-list entries for the current passage set: the served question rows
  // (answered dot + bookmark ribbon) and — once reached — a clickable
  // "Passage End Directions" end row. NO leading directions row.
  // The passage-end page is REACHED once every slot of the set is served and
  // the last one is answered (the end-of-passage interstitial is available).
  // Until then it isn't listed — mirroring the reached-questions gating.
  const passageEndReached =
    (passageGroupSize ?? 0) > 0 &&
    passageSlots.length >= (passageGroupSize ?? 0) &&
    passageSlots.length > 0 &&
    !!passageSlots[passageSlots.length - 1]?.answered;
  // Per-set question numbering: label rows 1..N within THIS set (slotNumber −
  // firstSlotOfSet + 1), not by global slot number. Jump target stays the
  // global slotNumber.
  const firstSlotOfSet = passageSlots.length
    ? Math.min(...passageSlots.map((s) => s.slotNumber))
    : 0;
  const reviewEntries: PassageReviewEntry[] = [
    // Leading "ELA Rdg Comp Directions" row — ONLY on the FIRST reading passage
    // set (and never for Part A, which uses standaloneItems). Clickable → the
    // reading-comp directions overlay.
    ...(passageSet?.index === 1 && standaloneItems == null
      ? [{
          key: -4,
          label: 'ELA Rdg Comp Directions',
          kind: 'directions' as const,
          target: 'reading-directions' as const,
          isCurrent: onReadingDirectionsPage,
        }]
      : []),
    ...passageSlots.map((s) => ({
      key: s.slotNumber,
      label: `Question ${s.slotNumber - firstSlotOfSet + 1}`,
      kind: 'question' as const,
      answered: s.answered,
      bookmarked: bookmarkedSlots.includes(s.slotNumber),
      isCurrent: s.slotNumber === currentSlotNumber,
    })),
    ...(passageEndReached
      ? [{
          key: -2,
          label: 'Passage End Directions',
          kind: 'end' as const,
          target: 'passage-end' as const,
          isCurrent: onPassageEndPage,
        }]
      : []),
  ];

  const [remaining, setRemaining] = useState<number | null>(() => {
    if (durationSeconds == null) return null;
    const stored = localStorage.getItem(storageKey);
    if (stored !== null) {
      const parsed = parseInt(stored, 10);
      if (Number.isFinite(parsed) && parsed >= 0) return parsed;
    }
    return Math.floor(durationSeconds);
  });
  // Hidden by default, matching the legacy player (timer shown only after
  // the student clicks "Show Timer").
  const [showTimer, setShowTimer] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const tickRef = useRef<number | null>(null);
  const dropdownRef = useOutsideClickHandler(() => setIsDropdownOpen(false));

  // Tick once per second. See AdaptiveTopBar.tsx pre-refactor notes for
  // the late-arriving-duration handling and the zero-boundary cleanup.
  useEffect(() => {
    if (durationSeconds == null) return;
    if (remaining === null) {
      const stored = localStorage.getItem(storageKey);
      const parsed = stored !== null ? parseInt(stored, 10) : NaN;
      const init = Number.isFinite(parsed) && parsed >= 0 ? parsed : Math.floor(durationSeconds);
      setRemaining(init);
      return;
    }
    if (remaining <= 0) {
      try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
      // Legacy parity: auto-submit the whole test when the clock expires.
      if (!expiredRef.current) {
        expiredRef.current = true;
        onTimeExpired?.();
      }
      return;
    }
    tickRef.current = window.setInterval(() => {
      setRemaining((prev) => {
        if (prev === null || prev <= 0) return prev;
        const next = Math.max(0, prev - 1);
        try {
          if (next === 0) localStorage.removeItem(storageKey);
          else localStorage.setItem(storageKey, String(next));
        } catch {
          /* private-browsing: timer still works in-memory */
        }
        return next;
      });
    }, 1000);
    return () => {
      if (tickRef.current !== null) window.clearInterval(tickRef.current);
    };
  }, [durationSeconds, remaining === 0, remaining === null, storageKey]);

  const displayTime = remaining ?? 0;
  const isUntimed = durationSeconds == null;

  // Save and Discard: every served + answered slot is committed to the
  // DB at save-time, so exiting at any point is non-destructive. The
  // server's serve endpoint picks back up at the right slot on return.
  // No batched autosave to flush, so save == discard for adaptive.
  const handleExit = () => {
    setIsDropdownOpen(false);
    navigate(-1);
  };

  const isTool = (t: AdaptiveTool) => toolsSelected === t;

  return (
    <div className="digitalSHSATModule__header">
      <div className="digitalSHSATModule__headerTop">
        <div className="digitalSHSATModule__headerBox">
          {/* header - left: prev/next arrows + tool function group.
              Back arrow is enabled iff the previous slot is in the same
              passage group as the current slot AND has been served
              (server computes this in ``navigability.canGoBack``).
              Once a passage is exited, all its slots lock and back goes
              dark again. The forward arrow IS the "Next" action; it
              doubles for the bottom-right Button on the legacy page. */}
          <div className="digitalSHSATModule__headerLeft">
            <div className="digitalSHSATModule__arrowButtons">
              <div className="digitalSHSATModule__buttonWithTooltip">
                <button
                  type="button"
                  className="digitalSHSATModule__arrowButton"
                  disabled={!canGoBack}
                  onClick={() => onBack?.()}
                  aria-label={canGoBack ? 'Previous' : 'Previous (not available)'}
                >
                  <FaArrowLeft />
                </button>
                {canGoBack && (
                  <span className="digitalSHSATModule__tooltip">Previous</span>
                )}
              </div>
              <div className="digitalSHSATModule__buttonWithTooltip">
                <button
                  type="button"
                  className="digitalSHSATModule__arrowButton"
                  disabled={!canAdvance}
                  onClick={() => onAdvance?.()}
                  aria-label="Next"
                >
                  <FaArrowRight />
                </button>
                {canAdvance && (
                  <span className="digitalSHSATModule__tooltip">Next</span>
                )}
              </div>
            </div>
            {/* Per-passage-set Review (tabbed) + Bookmark — shown for a
                multi-question ELA passage set (reading OR Part A). Both are
                scoped to the current set; bookmarks are ephemeral and reset
                when the student moves to the next set (parent-owned). */}
            {showPassageTools && (
              <div className="digitalSHSATModule__reviews">
                <div className="digitalSHSATModule__review" ref={reviewRef}>
                  <div className="digitalSHSATModule__buttonWithTooltip">
                    <div
                      className={
                        isReviewOpen
                          ? 'digitalSHSATModule__reviewBox--active'
                          : 'digitalSHSATModule__reviewBox'
                      }
                      onClick={() => {
                        setIsReviewOpen((prev) => !prev);
                        setReviewTab('All Questions');
                      }}
                    >
                      Review
                      <PiListBulletsBold />
                    </div>
                    <span className="digitalSHSATModule__tooltip">Review</span>
                  </div>
                  {isReviewOpen && (
                    <PassageReviewList
                      entries={reviewEntries}
                      activeTab={reviewTab}
                      onTabChange={setReviewTab}
                      onJump={(entry) => {
                        setIsReviewOpen(false);
                        if (entry.target === 'reading-directions') {
                          onReviewJumpToReadingDirections?.();
                        } else if (entry.kind === 'question') {
                          if (entry.key !== currentSlotNumber) onReviewJump?.(entry.key);
                        } else if (entry.target === 'passage-end') {
                          onReviewJumpToPassageEnd?.();
                        }
                      }}
                    />
                  )}
                </div>
                {onToggleBookmark && (
                  <div className="digitalSHSATModule__buttonWithTooltip">
                    <div
                      className={
                        isBookmarked
                          ? 'digitalSHSATModule__reviewBox--active'
                          : 'digitalSHSATModule__reviewBox'
                      }
                      onClick={onToggleBookmark}
                    >
                      {isBookmarked
                        ? <FaBookmark className="digitalSHSATModule__reviewBox--activeIcon" />
                        : <FaRegBookmark />}
                      Bookmark
                    </div>
                    <span className="digitalSHSATModule__tooltip">Bookmark Question for Review</span>
                  </div>
                )}
              </div>
            )}
            <div className="digitalSHSATModule__functionGroup">
              <div className="digitalSHSATModule__buttonWithTooltip">
                <button
                  type="button"
                  className={
                    isTool('Pointer')
                      ? 'digitalSHSATModule__functionButton--active digitalSHSATModule__leftBorderRadius'
                      : 'digitalSHSATModule__functionButton'
                  }
                  onClick={() => onToolChange?.('Pointer')}
                >
                  <FaArrowPointer className="icon-margin" />
                </button>
                <span className="digitalSHSATModule__tooltip">Pointer</span>
              </div>
              <div className="digitalSHSATModule__buttonWithTooltip">
                <button
                  type="button"
                  // Legacy parity: the eliminator only applies to multiple
                  // choice / multiple response (types 1, 3) — disabled
                  // (greyed) on every other question type.
                  className={
                    !eliminatorEnabled
                      ? 'digitalSHSATModule__functionButton--disabled'
                      : isTool('Answer Eliminator')
                      ? 'digitalSHSATModule__functionButton--active'
                      : 'digitalSHSATModule__functionButton'
                  }
                  onClick={() => { if (eliminatorEnabled) onToolChange?.('Answer Eliminator'); }}
                >
                  <RxCross2 className="icon-margin" />
                </button>
                <span className="digitalSHSATModule__tooltip">Answer Eliminator</span>
              </div>
              <div className="digitalSHSATModule__buttonWithTooltip">
                <button
                  type="button"
                  className={
                    isTool('Global Notepad')
                      ? 'digitalSHSATModule__functionButton--active digitalSHSATModule__rightBorderRadius'
                      : 'digitalSHSATModule__functionButton'
                  }
                  onClick={() => onToolChange?.('Global Notepad')}
                >
                  <PiNote className="icon-margin" />
                </button>
                <span className="digitalSHSATModule__tooltip">Global Notepad</span>
              </div>
            </div>
          </div>

          {/* header - right: timer + student name + user dropdown */}
          <div className="digitalSHSATModule__headerRight" ref={dropdownRef}>
            <div className="digitalSHSATModule__timer">
              {showTimer && (
                <div className="digitalSHSATModule__timing">
                  {isUntimed ? 'Untimed' : formatTime(displayTime)}
                </div>
              )}
              <div className="digitalSHSATModule__buttonWithTooltip">
                <button
                  type="button"
                  className={
                    showTimer
                      ? 'digitalSHSATModule__timerButton--active'
                      : 'digitalSHSATModule__timerButton'
                  }
                  onClick={() => setShowTimer((v) => !v)}
                >
                  <TfiTimer />
                </button>
                <span className="digitalSHSATModule__tooltip">
                  {showTimer ? 'Hide Timer' : 'Show Timer'}
                </span>
              </div>
            </div>
            {studentName && (
              <div className="digitalSHSATModule__guest">{studentName}</div>
            )}
            <div className="digitalSHSATModule__buttonWithTooltip">
              <div
                className={
                  isDropdownOpen
                    ? 'digitalSHSATModule__guestButton--active'
                    : 'digitalSHSATModule__guestButton'
                }
                onClick={() => setIsDropdownOpen((v) => !v)}
              >
                <IoMdPerson />
                <IoMdArrowDropdown className="digitalSHSATModule__profileArrow" />
              </div>
              <span className="digitalSHSATModule__tooltip">User dropdown menu</span>
            </div>
            {isDropdownOpen && (
              <div className="digitalSHSATModule__dropdown">
                <div className="digitalSHSATModule__dropdownTools">
                  {/* Contrast Settings — opens the container-owned theme
                      modal (not a toggle). Ordered first to match the
                      official TestNav tools menu. */}
                  {onOpenContrast && (
                    <div
                      className="digitalSHSATModule__dropdownContent"
                      onClick={() => { setIsDropdownOpen(false); onOpenContrast(); }}
                    >
                      Change the background and foreground color
                    </div>
                  )}
                  {DROPDOWNTOOLS.map((tool) => (
                    <div
                      key={tool[0]}
                      className="digitalSHSATModule__dropdownContent"
                      onClick={() => onDropdownToolChange?.(tool[0])}
                    >
                      {dropdownToolsSelected.includes(tool[0]) ? tool[1] : tool[0]}
                      {dropdownToolsSelected.includes(tool[0]) && (
                        <FaCheck className="digitalSHSATModule__checkIcon" />
                      )}
                    </div>
                  ))}
                </div>
                <div className="digitalSHSATModule__exit" onClick={handleExit}>
                  Save and Exit
                </div>
                <div
                  className="digitalSHSATModule__exit"
                  onClick={() => {
                    setIsDropdownOpen(false);
                    // Adaptive discard is data-destructive (wipes served slots
                    // + answers server-side); the parent owns the endpoint call
                    // + localStorage clear + navigation. Falls back to a plain
                    // exit only if no handler was provided.
                    if (onDiscardExit) onDiscardExit();
                    else handleExit();
                  }}
                >
                  Discard and Exit
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="digitalSHSATModule__headerBoundary" />
      <div className="digitalSHSATModule__headerBottom">
        {(() => {
          // Bottom strip mirrors legacy Module/index.tsx: TEST NAME /
          // SECTION NAME / N OF M (the percent progress bar was removed from
          // both flows). ``globalSlot`` is the TEST-GLOBAL slot number (ELA
          // 1..N, Math N+1..2N); the section-level count below converts it to
          // a within-module position.
          const globalSlot = slotNumber ?? null;
          const total = totalSlots;
          // Section-level "N OF M" count: WITHIN-CURRENT-MODULE position out of
          // this module's own slot count (not the test-global slot / totalSlots).
          // So Math reads "1 OF 50" regardless of ELA/Math order, matching the
          // legacy header. Progress logic above stays test-global.
          const sectionPosition = globalSlot != null && moduleFirstSlotNumber != null
            ? globalSlot - moduleFirstSlotNumber + 1
            : globalSlot;
          const sectionTotal = moduleSlotCount ?? total;
          // ELA passage-set framing mirrors the legacy top bar exactly:
          // section line "<SECTION> – PASSAGE SET X OF N" and a per-set
          // "n OF m" count. Only when the server supplied a resolved set
          // index (ELA passage slots); Math/standalone keep the global
          // section label + "N OF M" count. The progress bar stays
          // test-global regardless.
          const showPassageSet = passageSet != null && passageSet.index != null;
          // Unified stand-alone block takes precedence: "<SECTION> – STAND
          // ALONE ITEMS" + per-block "n OF size" (not a passage-set number).
          const showStandalone = standaloneItems != null;
          return (
            <div className="digitalSHSATModule__headerBottomContents">
              <div>
                {(testName || 'DIGITAL SHSAT PRACTICE TEST').toUpperCase()}
                {'  '}{'  '}/{'  '}{'  '}
              </div>
              {sectionName && (
                <div>{showStandalone
                  ? `${sectionDisplayLabel(sectionName).toUpperCase()} – STAND ALONE ITEMS`
                  : showPassageSet
                  ? `${sectionDisplayLabel(sectionName).toUpperCase()} – PASSAGE SET ${passageSet.index} OF ${passageSet.count}`
                  : sectionDisplayLabel(sectionName).toUpperCase()}{'  '}{'  '}/{'  '}{'  '}</div>
              )}
              {globalSlot != null && total > 0 && (
                <div>
                  {showStandalone
                    ? `${standaloneItems.position} OF ${standaloneItems.size}`
                    : showPassageSet
                    ? `${passageSet.position} OF ${passageSet.size}`
                    : `${sectionPosition} OF ${sectionTotal}`}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
